/**
 * No Ajax Variants - Handle variant selection without AJAX calls
 * 
 * This script preloads all product variant data and updates the UI without making AJAX requests
 * when a variant is selected. It overrides the default product-info.js behavior.
 */



if (!customElements.get('no-ajax-variants')) {
  // Store the original publish function if it exists
  const originalPublish = window.publish;
  
  // Override the publish function to intercept variant change events
  window.publish = function(eventName, payload) {
    // If it's a variant change event, check if we should intercept
    if (eventName === 'option-value-selection-change') {
      // Get the product-info element
      const productInfo = document.querySelector('product-info[data-update-url="false"]');
      if (productInfo) {
        // If this is a no-ajax product, let our component handle it instead
        const noAjaxVariants = document.querySelector('no-ajax-variants');
        if (noAjaxVariants) {
          // Still trigger our handler, but prevent original event from propagating to product-info.js
          noAjaxVariants.handleVariantChange(payload);
          return;
        }
      }
    }
    
    // For all other events, proceed with normal publication
    if (originalPublish) {
      return originalPublish(eventName, payload);
    }
  };
  
  // Override the ProductInfo renderProductInfo method to prevent AJAX calls
  function patchProductInfo() {
    // Wait for all components to be defined
    if (customElements.get('product-info')) {
      const ProductInfoProto = customElements.get('product-info').prototype;
      
      // Patch the renderProductInfo method
      const originalRenderProductInfo = ProductInfoProto.renderProductInfo;
      ProductInfoProto.renderProductInfo = function({ requestUrl, targetId, callback }) {
        // If the URL contains option_values, skip the AJAX call completely
        if (requestUrl && requestUrl.includes('option_values=')) {
          return; // Prevent the AJAX call
        }
        
        // Otherwise, call the original method (for other operations)
        return originalRenderProductInfo.call(this, { requestUrl, targetId, callback });
      };
      
      // Patch the handleOptionValueChange method at its source
      const originalHandleOptionValueChange = ProductInfoProto.handleOptionValueChange;
      ProductInfoProto.handleOptionValueChange = function(event) {
        if (!this.contains(event.data.event.target)) return;

        // For variant changes, use our no-ajax method instead
        if (event.data.selectedOptionValues && 
            this.dataset && 
            this.dataset.updateUrl === 'false') {
          // Skip the normal flow that would make an AJAX call
          return;
        }
        
        // For everything else, use the original method
        return originalHandleOptionValueChange.call(this, event);
      };
      
    } else {
      // Try again after a short delay if product-info isn't defined yet
      setTimeout(patchProductInfo, 50);
    }
  }
  
  // Start the patching process
  patchProductInfo();

  customElements.define(
    'no-ajax-variants',
    class NoAjaxVariants extends HTMLElement {
      constructor() {
        super();
        this.variantData = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
        this.productOptions = JSON.parse(this.querySelector('[data-product-options]')?.textContent || '[]');
        this.productElement = this.closest('product-info');
        this.variantSelectors = this.productElement?.querySelector('variant-selects');
        this.currentVariant = null;
        this.unsubscriber = null;
        
        // Mapping from option values to variant IDs for faster lookup
        this.optionValueMap = {};


      }

      connectedCallback() {
        // Wait for DOM content to be fully loaded
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
      }

      init() {
        // Parse variant data if it's not yet parsed
        if (!this.variantData || !this.variantData.length) {
          try {
            const variantDataElement = this.querySelector('[data-variant-data]');
            if (variantDataElement) {
              this.variantData = JSON.parse(variantDataElement.textContent || '[]');
              // console.log('Loaded variant data for', this.variantData.length, 'variants');
              
              // Ensure variants have option_value_ids array
              this.variantData.forEach(variant => {
                if (!variant.option_value_ids && variant.options_with_values) {
                  variant.option_value_ids = [];
                  variant.options_with_values.forEach(option => {
                    option.values.forEach(value => {
                      if (value.id) {
                        variant.option_value_ids.push(value.id.toString());
                      }
                    });
                  });
                }
              });
            }
          } catch (e) {
            console.error('Error parsing variant data:', e);
            return;
          }
        }
        
        // Parse product options if needed
        if (!this.productOptions || !this.productOptions.length) {
          try {
            const optionsElement = this.querySelector('[data-product-options]');
            if (optionsElement) {
              this.productOptions = JSON.parse(optionsElement.textContent || '[]');
              // console.log('Loaded product options:', this.productOptions);
            }
          } catch (e) {
            console.error('Error parsing product options:', e);
          }
        }

        // Only initialize if we have product element and variants
        if (!this.productElement || !this.variantData.length) {
          console.warn('Missing product element or variant data');
          return;
        }

        // Build option value to variant mapping for faster lookup
        this.buildOptionValueMap();

        // Override product-info behavior by setting update-url to false
        this.productElement.setAttribute('data-update-url', 'false');

        // Initialize with the first available variant or currently selected variant
        this.currentVariant = this.getCurrentVariantFromURL() || 
                              this.variantData.find(variant => variant.available) || 
                              this.variantData[0];

        // Initialize media map for quick reference
        this.initMediaMap();

        // Subscribe to variant change events (multiple approaches for robustness)
        this.unsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleVariantChange.bind(this)
        );

        // Also attach to direct DOM events for bundle options and other special selectors
        this.attachDirectEventListeners();

        // Initial UI update with the current variant
        if (this.currentVariant) {
          this.updateUI(this.currentVariant);
        }
      }

      attachDirectEventListeners() {
        // Listen for changes on radios and selects
        const variantSelects = this.closest('product-info').querySelector('variant-selects');
        if (!variantSelects) return;

        // Attach to radio buttons (often used for color/bundle options)
        const radioInputs = variantSelects.querySelectorAll('input[type="radio"]');
        
        radioInputs.forEach(radio => {
          // Check if this is a color swatch
          const isColorSwatch = radio.closest('.variant-item--color') || 
                               radio.closest('[data-option-name="Color"]') ||
                               radio.parentNode.querySelector('.color-swatch');
                               
          if (isColorSwatch) {
            // console.log(`Found color swatch radio: ${radio.value}`);
          }
          
          // Add event listener to all radios (including colors)
          radio.addEventListener('change', (e) => {
            // Ensure transitions are added
            this.ensureTransitionCSS();
            // Call the variant change handler
            this.handleVariantChange(e);
          });
        });

        // Attach to dropdowns
        const selectElements = variantSelects.querySelectorAll('select');
        
        selectElements.forEach(select => {
          // Check if this is a color select
          const isColorSelect = select.name && 
                               (select.name.includes('Color') || 
                                select.id && select.id.includes('Color'));
                               
          if (isColorSelect) {
            // console.log(`Found color select: ${select.name}`);
          }
          
          // Add event listener to all selects
          select.addEventListener('change', (e) => {

            console.log(e)
            // Ensure transitions are added
            this.ensureTransitionCSS();
            // Call the variant change handler
            this.handleVariantChange(e);
          });
        });

        // Special handling for bundle options
        const bundleOptions = document.querySelector('bundle-options');
        if (bundleOptions) {
          
          // Listen for clicks on bundle option headers
          const bundleHeaders = bundleOptions.querySelectorAll('.bundle-option__header');
          bundleHeaders.forEach(header => {
            header.addEventListener('click', (e) => {
              // console.log("click")
              // Ensure transitions are added
              this.ensureTransitionCSS();
              // Short delay to let the bundle-options component update its state
              setTimeout(() => this.handleBundleOptionChange(), 20);
            });
          });
        }

        // Listen for variant:changed events
        document.addEventListener('variant:changed', (e) => {
          // Ensure transitions are added
          this.ensureTransitionCSS();
          // Call handler
          this.handleVariantChange(e);
        });

        // Also find and attach to color swatches that may have a different structure
        const colorSwatches = document.querySelectorAll('.color-swatch, .swatch-element, .swatch-input');
        if (colorSwatches.length > 0) {
          // console.log(`Found ${colorSwatches.length} additional color swatches`);
          
          colorSwatches.forEach(swatch => {
            swatch.addEventListener('click', (e) => {
              // Ensure transitions are added before the variant changes
              this.ensureTransitionCSS();
            });
          });
        }
      }

      handleBundleOptionChange() {
        // Check if we're already processing a bundle option change to prevent loops
        if (this._processingBundleOptionChange) {
          // console.log('Already processing bundle option change, preventing recursive call');
          return;
        }
        
        // Set guard flag
        this._processingBundleOptionChange = true;
        
        try {
          
          // Get the currently selected bundle option
          const bundleOptions = document.querySelector('bundle-options');
          if (!bundleOptions) {
            return;
          }
          
          // Get the checked radio input in the bundle options
          const checkedInput = bundleOptions.querySelector('input[type="radio"]:checked');
          if (!checkedInput) {
            return;
          }
          
          const bundleValue = checkedInput.value;
          
          // 1. Get all variants with this bundle option
          const allVariants = this.variantData || [];
          const bundleMatchingVariants = allVariants.filter(variant => {
            return variant.options && variant.options.includes(bundleValue);
          });
                    
          if (bundleMatchingVariants.length === 0) {
            return;
          }
          
          // 2. Try to get currently selected color
          const currentColorOption = this.getCurrentlySelectedColor();
          
          // 3. Find a variant with both the selected bundle and color, if color is available
          let targetVariant = null;
          
          if (currentColorOption) {
            // Try to find a variant with both the bundle and the current color
            targetVariant = bundleMatchingVariants.find(variant => {
              return variant.options && variant.options.includes(currentColorOption);
            });
            
          }
          
          // If no match with color, just use the first variant with matching bundle
          if (!targetVariant) {
            targetVariant = bundleMatchingVariants[0];
          }
          
          // Make sure we have a variant to work with
          if (!targetVariant) {
            return;
          }
                    
          // 4. Update the UI with the new variant
          this.currentVariant = targetVariant;
          
          // Update form inputs and UI
          this.updateFormInput(targetVariant);
          this.updateUI(targetVariant);
          
          // Update URL
          this.updateURL(targetVariant);
          
          // Dispatch event
          this.dispatchEvent(new CustomEvent('variant:updated', {
            bubbles: true,
            detail: { variant: targetVariant }
          }));
          
        } catch (e) {
          console.error('Error in handleBundleOptionChange:', e);
        } finally {
          // Make sure to clear the flag at the end of all paths
          this._processingBundleOptionChange = false;
        }
      }

      // Helper method to get the currently selected color
      getCurrentlySelectedColor() {
        try {
          // Try different ways to find color selection
          
          // 1. Check for color radio buttons
          const colorRadios = document.querySelectorAll('input[type="radio"][name="Color"], input[type="radio"][data-option-name="Color"]');
          for (const radio of colorRadios) {
            if (radio.checked) {
              return radio.value;
            }
          }
          
          // 2. Check fieldsets with color labels
          const colorFieldsets = Array.from(document.querySelectorAll('fieldset'))
            .filter(fieldset => {
              const label = fieldset.querySelector('.form__label');
              return label && label.textContent.trim().toLowerCase().includes('color');
            });
          
          for (const fieldset of colorFieldsets) {
            const checkedInput = fieldset.querySelector('input:checked');
            if (checkedInput) {
              return checkedInput.value;
            }
          }
          
          // 3. Check if first option is typically color in this theme
          if (this.currentVariant && this.currentVariant.options && this.currentVariant.options.length > 0) {
            // console.log(`Using first option as color from current variant: ${this.currentVariant.options[0]}`);
            return this.currentVariant.options[0];
          }
          
          // 4. Check through current variant to extract the likely color option
          if (this.currentVariant && this.currentVariant.title) {
            const titleParts = this.currentVariant.title.split('/');
            if (titleParts.length > 0) {
              const possibleColor = titleParts[0].trim();
              // console.log(`Extracted possible color from variant title: ${possibleColor}`);
              return possibleColor;
            }
          }
          
          // console.log('Could not determine current color selection');
          return null;
        } catch (e) {
          console.error('Error in getCurrentlySelectedColor:', e);
          return null;
        }
      }

      disconnectedCallback() {
        if (this.unsubscriber) {
          this.unsubscriber();
        }
      }

      initMediaMap() {
        // Create a map of variant IDs to media
        this.mediaMap = {};
        this.variantData.forEach(variant => {
          if (variant.featured_media) {
            this.mediaMap[variant.id] = {
              id: variant.featured_media.id,
              position: variant.featured_media.position,
              alt: variant.featured_media.alt
            };
          }
        });
      }

      handleVariantChange(event) {
        // Check if this event was generated by our code to prevent infinite loops
        if (event && event._generatedByNoAjaxVariants) {
          return;
        }
        
        // Check for bundle value in the event data
        let bundleValue = null;
        if (event.data && event.data.bundleValue) {
          bundleValue = event.data.bundleValue;
        }
        
        // Extract selected option values from various event structures
        let selectedOptionValues = [];
        
        if (event.data && event.data.selectedOptionValues) {
          // Standard subscription event
          selectedOptionValues = event.data.selectedOptionValues;
        } else if (event.detail && event.detail.selectedOptionValues) {
          // Custom event structure
          selectedOptionValues = event.detail.selectedOptionValues;
        } else if (event.target && event.target.checked !== undefined) {
          // Direct DOM event (like from radio button)
          
          // Check if this is a bundle option radio button
          const isBundleOption = event.target.closest('.bundle-option');
          if (isBundleOption) {
            bundleValue = event.target.value;
            
            // Delegate to handleBundleOptionChange for better handling
            this.handleBundleOptionChange();
            return;
          }
          
          const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
          if (variantSelects) {
            const selectedElements = Array.from(
              variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
            );
            
            selectedOptionValues = selectedElements.map(el => {
              return el.dataset.optionValueId || el.value;
            });
          }
        }
        
        // If we still don't have option values, try to extract them from the DOM
        if (!selectedOptionValues || selectedOptionValues.length === 0) {
          
          const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
          if (variantSelects) {
            const selectedElements = Array.from(
              variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
            );
            
            selectedOptionValues = selectedElements.map(el => {
              return el.dataset.optionValueId || el.value;
            });
          }
        }
        
        // Find the variant that matches selected options
        const matchedVariant = this.findVariantByOptions(selectedOptionValues, { bundleValue });
        
        if (!matchedVariant) {
          this.handleUnavailableVariant();
          return;
        }
        // Update current variant
        this.currentVariant = matchedVariant;

        // Update the UI
        this.updateUI(matchedVariant);

        // Update URL
        this.updateURL(matchedVariant);
        
        // Trigger a custom event that other components can listen to
        this.dispatchEvent(new CustomEvent('variant:updated', {
          bubbles: true,
          detail: { variant: matchedVariant }
        }));
        
      }

      findVariantByOptions(selectedOptionValues, options = {}) {
        // Find variant that matches the selected options
        
        // Extract bundle value from options
        const bundleValue = options?.bundleValue;

        
        if (!selectedOptionValues || selectedOptionValues.length === 0) {
          return null;
        }
                
        // Special case: If this was triggered by a bundle selection, prioritize finding
        // a variant that contains the bundle value
        if (bundleValue) {
          const bundleVariants = this.variantData.filter(variant => 
            variant.options && variant.options.some(opt => 
              opt === bundleValue ||
                (
                  typeof opt === 'string' &&
                  typeof bundleValue === 'string' &&
                  opt.toLowerCase() === bundleValue.toLowerCase()
                )
            )
          );
          
          if (bundleVariants.length > 0) {            
            // Prefer available variants
            const availableBundleVariants = bundleVariants.filter(v => v.available);
            if (availableBundleVariants.length > 0) {
              // console.log(`Using first available bundle variant: ${availableBundleVariants[0].id}`);
              return availableBundleVariants[0];
            }
            
            // If no available variants, use the first one
            return bundleVariants[0];
          }
        }
        
        // Get all selected option values from the DOM for more context
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) {
          return null;
        }
        
        // Get all form elements that are currently selected
        const selectedElements = Array.from(
          variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
        );
        
        // Extract option values as strings from selected elements
        const selectedOptionValueStrings = selectedElements.map(el => {
          return el.value;
        });
        
        // Fast path: Use our option value map to find candidates
        let candidateVariants = [];
        
        // Find variants that match ALL selected option values (intersection of sets)
        if (selectedOptionValues.length > 0) {
          // Start with candidates from the first option value
          const firstOptionValue = selectedOptionValues[0];
          if (this.optionValueMap[firstOptionValue]) {
            candidateVariants = [...this.optionValueMap[firstOptionValue]];
          } else {
            // Check if it's a string that needs trimming or case insensitivity
            const possibleMatches = Object.keys(this.optionValueMap).filter(key => 
              key.toLowerCase().trim() === firstOptionValue.toLowerCase().trim()
            );
            if (possibleMatches.length > 0) {
              candidateVariants = [...this.optionValueMap[possibleMatches[0]]];
            }
          }
          
          // Then intersect with candidates from other option values
          for (let i = 1; i < selectedOptionValues.length; i++) {
            const valueId = selectedOptionValues[i];
            if (this.optionValueMap[valueId]) {
              const beforeCount = candidateVariants.length;
              candidateVariants = candidateVariants.filter(variant => 
                this.optionValueMap[valueId].includes(variant)
              );
            } else {
              // Check if it's a string that needs trimming or case insensitivity
              const possibleMatches = Object.keys(this.optionValueMap).filter(key => 
                (key ?? '').toString().toLowerCase().trim() === (valueId ?? '').toString().toLowerCase().trim()
              );
              if (possibleMatches.length > 0) {
                const beforeCount = candidateVariants.length;
                candidateVariants = candidateVariants.filter(variant => 
                  this.optionValueMap[possibleMatches[0]].includes(variant)
                );
              }
            }
          }
        }
        
        // If we still don't have candidates, try with the string values
        if (candidateVariants.length === 0 && selectedOptionValueStrings.length > 0) {
          
          // Start with candidates from the first option value
          const firstOptionString = selectedOptionValueStrings[0];
          if (this.optionValueMap[firstOptionString]) {
            candidateVariants = [...this.optionValueMap[firstOptionString]];
          }
          
          // Then intersect with candidates from other option values
          for (let i = 1; i < selectedOptionValueStrings.length; i++) {
            const valueString = selectedOptionValueStrings[i];
            if (this.optionValueMap[valueString]) {
              const beforeCount = candidateVariants.length;
              candidateVariants = candidateVariants.filter(variant => 
                this.optionValueMap[valueString].includes(variant)
              );
            }
          }
        }
        
        // Bundle value takes precedence - if we have both a bundle value and candidates,
        // filter to keep only candidates that contain the bundle value
        if (bundleValue && candidateVariants.length > 0) {
          const bundleCandidates = candidateVariants.filter(variant => 
            variant.options && variant.options.some(opt => 
              opt === bundleValue ||
                (
                  typeof opt === 'string' &&
                  typeof bundleValue === 'string' &&
                  opt.toLowerCase() === bundleValue.toLowerCase()
                )
            )
          );
          
          if (bundleCandidates.length > 0) {
            candidateVariants = bundleCandidates;
          }
        }
        
        
        // If we found exactly one variant, that's our match
        if (candidateVariants.length === 1) {
          return candidateVariants[0];
        }
        
        // If we found multiple candidates, prefer available ones
        if (candidateVariants.length > 1) {
          const availableCandidates = candidateVariants.filter(v => v.available);
          
          if (availableCandidates.length > 0) {
            return availableCandidates[0];
          }
          
          // If no available candidates, return the first one
          return candidateVariants[0];
        }
        
        // Create a map to count how many options each variant matches
        const variantMatchCounts = {};
        
        // For each option value, count matching variants
        selectedOptionValues.forEach(optionValue => {
          if (this.optionValueMap[optionValue]) {
            this.optionValueMap[optionValue].forEach(variant => {
              if (!variantMatchCounts[variant.id]) {
                variantMatchCounts[variant.id] = { variant, count: 0 };
              }
              variantMatchCounts[variant.id].count++;
            });
          }
        });
        
        // Do the same for string values
        selectedOptionValueStrings.forEach(optionValue => {
          if (this.optionValueMap[optionValue]) {
            this.optionValueMap[optionValue].forEach(variant => {
              if (!variantMatchCounts[variant.id]) {
                variantMatchCounts[variant.id] = { variant, count: 0 };
              }
              variantMatchCounts[variant.id].count++;
            });
          }
        });
        
        // If we have a bundle value, boost the score for variants with that bundle value
        if (bundleValue) {
          Object.values(variantMatchCounts).forEach(match => {
            if (match.variant.options && match.variant.options.some(opt => 
              opt === bundleValue ||
                (
                  typeof opt === 'string' &&
                  typeof bundleValue === 'string' &&
                  opt.toLowerCase() === bundleValue.toLowerCase()
                )
            )) {
              match.count += 10; // Significant boost for bundle match
            }
          });
        }
        
        // Convert to array and sort by match count (descending)
        const sortedMatches = Object.values(variantMatchCounts).sort((a, b) => b.count - a.count);
        
      
        // If we found any partial matches
        if (sortedMatches.length > 0) {
          // First try to find an available variant with the highest match count
          const bestAvailableMatch = sortedMatches.find(match => match.variant.available);
          if (bestAvailableMatch) {
            return bestAvailableMatch.variant;
          }
          
          // Otherwise, use the highest match count regardless of availability
          return sortedMatches[0].variant;
        }
        
        // Fallback: Return first variant that is available
        const fallbackVariant = this.variantData.find(v => v.available) || this.variantData[0];
        
        return fallbackVariant;
      }

      updateUI(variant) {

            const bundleTotal = document.getElementById('bundleTotal');

          if (bundleTotal) {
            const basePrice = (variant.price / 100);

             const discountCode = localStorage.getItem('discount_code');


            if (discountCode) {
              const discountedPrice = basePrice -100;

              bundleTotal.innerHTML = `
                $${discountedPrice}
                <strike style="opacity:0.5; margin-left:6px;">$${basePrice}</strike>
              `;
            } else {
              bundleTotal.innerHTML = `$${basePrice}`;
            }
          }
        // Force add transition CSS early to ensure it's available
        this.ensureTransitionCSS();
        
        // Update price
        this.updatePrice(variant);

        // Update SKU
        this.updateSKU(variant);

        // Update inventory
        this.updateInventory(variant);

        // Update form input value
        this.updateFormInput(variant);

        // Update media/images with transition effects
        this.updateMedia(variant);

        // Enable/disable add to cart button
        this.updateAddToCartButton(variant);
      }

      updatePrice(variant) {
        const priceContainer = this.productElement.querySelector(`#price-${this.productElement.dataset.section}`);
        if (!priceContainer) return;

        const regularPrice = priceContainer.querySelector('.price__regular .price-item');
        const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');
        const comparePrice = priceContainer.querySelector('.price__sale .price-item--regular');

        if (variant.compare_at_price > variant.price) {
          // It's on sale
          priceContainer.querySelector('.price__regular').classList.add('hidden');
          priceContainer.querySelector('.price__sale').classList.remove('hidden');
          salePrice.textContent = this.formatMoney(variant.price);
          comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        } else {
          // Regular price
          priceContainer.querySelector('.price__regular').classList.remove('hidden');
          priceContainer.querySelector('.price__sale').classList.add('hidden');
          regularPrice.textContent = this.formatMoney(variant.price);
        }
      }

      updateSKU(variant) {
        const skuElement = document.getElementById(`Sku-${this.productElement.dataset.section}`);
        if (!skuElement) return;

        if (variant.sku) {
          skuElement.textContent = variant.sku;
          skuElement.classList.remove('hidden');
        } else {
          skuElement.classList.add('hidden');
        }
      }

      updateInventory(variant) {
        const inventoryElement = document.getElementById(`Inventory-${this.productElement.dataset.section}`);
        if (!inventoryElement) return;

        // This is a simplified version - you may need to adjust based on your inventory display logic
        if (variant.available) {
          inventoryElement.classList.remove('hidden');
        } else {
          inventoryElement.classList.add('hidden');
        }
      }

      updateFormInput(variant) {
        if (!variant) {
          console.error('Cannot update form input: no variant provided');
          return;
        }
        
        // Track state to prevent endless loops
        if (this._updatingFormInputs) {
          console.log('Already updating form inputs, preventing recursive call');
          return;
        }
        
        // Set guard flag
        this._updatingFormInputs = true;
        
        // Update the hidden form input with the variant ID
        const formInputs = document.querySelectorAll('input[name="id"]');
        formInputs.forEach((input, index) => {
          const oldValue = input.value;
          input.value = variant.id;
          
          // Create and dispatch a change event
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        });
        
        // Force update the required Radio buttons (for color and bundle options)
        const options = variant.options || [];
        
        
        options.forEach((optionValue, index) => {
          const position = index + 1; // Option positions are 1-indexed
          
          // Update radio buttons (often used for colors)
          const radioButtons = document.querySelectorAll(`input[type="radio"][name="options[option${position}]"], input[type="radio"][name="options[Option ${position}]"]`);
          
          let radioUpdated = false;
          radioButtons.forEach(radio => {
            console.log(`  Radio button: value="${radio.value}", checked=${radio.checked}`);
            if (radio.value === optionValue) {
              const wasChecked = radio.checked;
              radio.checked = true;

   



              console.log(`  Updated radio: value="${radio.value}", wasChecked=${wasChecked}, nowChecked=${radio.checked}`);
              radioUpdated = true;
              
              // Dispatch a change event
              const event = new Event('change', { bubbles: true });
              // Set a flag on the event to identify it as generated by our code
              event._generatedByNoAjaxVariants = true;
              radio.dispatchEvent(event);
              console.log(`  Dispatched change event on radio button`);
            }
          });
          
          
          // Update select elements
          const selectElements = document.querySelectorAll(`select[name="options[option${position}]"], select[name="options[Option ${position}]"]`);
          
          let selectUpdated = false;
          selectElements.forEach(select => {
            console.log(`  Select: name="${select.name}", current value="${select.value}"`);
            const option = Array.from(select.options).find(opt => opt.value === optionValue);
            if (option) {
              const oldValue = select.value;
              select.value = optionValue;
              option.selected = true;
              console.log(`  Updated select: from "${oldValue}" to "${optionValue}"`);
              selectUpdated = true;
              
              // Dispatch a change event
              const event = new Event('change', { bubbles: true });
              // Set a flag on the event to identify it as generated by our code
              event._generatedByNoAjaxVariants = true;
              select.dispatchEvent(event);
              console.log(`  Dispatched change event on select element`);
            } else {
              console.log(`  WARNING: Option "${optionValue}" not found in select options`);
              console.log(`  Available options:`, Array.from(select.options).map(o => o.value));
            }
          });
          
          if (!selectUpdated && selectElements.length > 0) {
            console.log(`  WARNING: No matching select option found for option ${position} value "${optionValue}"`);
          }
        });
        
        // Also try to update bundle options if they exist
          const bundleOptions = document.querySelector('bundle-options');
          const bundleRadios = document.querySelectorAll('.bundle-option input[type="radio"]');

          // bundleRadios.forEach(radio => {
          //   const parent = radio.closest('.bundle-option');
          //   const bundleValue = parent?.dataset.bundleValue || 'unknown';

          // });

          if (bundleOptions) {
            let activeBundle = null;

            bundleRadios.forEach(radio => {
              const parent = radio.closest('.bundle-option');
              if (!parent) return;

              const check = parent.querySelector('.CheckMarkDiv');
              const matches = (variant.options || []).some(option => option === radio.value);

              if (matches) {
                activeBundle = parent;
                radio.checked = true;
                parent.classList.add('bundle-option--active');

                if (check) {
                  check.classList.remove('t-border', 't-border-current');
                  check.classList.add('t-bg-Main');
                  check.innerHTML = `
                    <svg
                      width="18"
                      height="18"
                      class="t-w-3 t-h-3 t-text-White"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M2 6L5 9L10 3"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>`;
                }

                const event = new Event('change', { bubbles: true });
                event._generatedByNoAjaxVariants = true;
                radio.dispatchEvent(event);
              } else {
                parent.classList.remove('bundle-option--active');

                if (check) {
                  check.classList.add('t-border', 't-border-current');
                  check.classList.remove('t-bg-Main');
                  check.innerHTML = '';
                }
              }
            });

            if (typeof bundleOptions.updateUI === 'function') {
              try {
                bundleOptions.updateUI();
              } catch (e) {
                console.error('Error calling bundleOptions.updateUI():', e);
              }
            }

            if (!activeBundle) {
              console.warn('⚠️ No matching bundle found for variant:', variant.options);
            }
          } else {
            console.log('No bundle-options element found');
          }

        
        // Update any custom option elements as needed
        const optionElements = document.querySelectorAll('[data-option-value]');
        
        optionElements.forEach(element => {
          const optionIndex = parseInt(element.dataset.option) - 1;
          console.log(`  Element: data-option=${element.dataset.option}, data-option-value="${element.dataset.optionValue}"`);
          
          if (options[optionIndex] === element.dataset.optionValue) {
            console.log(`  Clicking matching option element: data-option-value="${element.dataset.optionValue}"`);
            element.click();
          }
        });
        
        document.dispatchEvent(new CustomEvent('variant:input:updated', {
          bubbles: true,
          detail: { variant }
        }));
        
        // Clear guard flag
        this._updatingFormInputs = false;
        
      }

      updateMedia(variant) {
        if (!variant || !variant.featured_media) {
          console.log('No featured media for variant, skipping media update');
          return;
        }
        
        // Get the media ID to look for
        const mediaId = `${variant.featured_media.id}`;
        
        // Find the media gallery - try multiple selectors to ensure we find it
        const mediaGallery = document.querySelector('media-gallery') || 
                             document.querySelector('.product__media-gallery') ||
                             document.querySelector('#MediaGallery-template--');
        
        if (!mediaGallery) {
          console.log('No media gallery found, cannot update media');
          return;
        }
        
        
        // Add transition CSS if not already present
        this.ensureTransitionCSS();
        
        // Check if we're using a slider component
        const slider = mediaGallery.querySelector('slider-component, slideshow-component');
        if (slider) {
          
          // Find the matching media item
          const mediaItems = mediaGallery.querySelectorAll('[data-media-id]');
          
          // Extract section ID from the media gallery ID if possible
          let sectionId = '';
          if (mediaGallery.id && mediaGallery.id.includes('MediaGallery-')) {
            sectionId = mediaGallery.id.replace('MediaGallery-', '');
          }
          
          // Look for the media item with the matching media ID
          const matchingItem = Array.from(mediaItems).find(
            item => {
              // Check if data-media-id contains our media ID (it might be prefixed with section ID)
              const dataMediaId = item.dataset.mediaId;
              
              // Try different matching patterns
              return dataMediaId === mediaId || // Direct match
                     dataMediaId === `${sectionId}-${mediaId}` || // With section prefix
                     dataMediaId.includes(`-${mediaId}`) || // With any prefix
                     // Using ID attribute as fallback
                     (item.id && item.id.includes(`Slide-`) && item.id.includes(`-${mediaId}`));
            }
          );
          
          if (matchingItem) {            
            // Get the slide containing this media item
            const slides = slider.querySelectorAll('.slider__slide, .slideshow__slide');
            
            // Identify currently active slide
            const activeSlide = slider.querySelector('.slider__slide.is-active, .slideshow__slide.is-active, .slider__slide.active, .slideshow__slide.active');
            
            const slideIndex = Array.from(slides).findIndex(
              slide => slide.contains(matchingItem)
            );
            
            if (slideIndex >= 0) {              
              // Add transition class to slider track
              const sliderTrack = slider.querySelector('.slider-track, .slideshow__track');
              if (sliderTrack) {
                sliderTrack.classList.add('variant-image-transition');
              }
              
              // Add fade-out to current slide, fade-in to new slide
              if (activeSlide && activeSlide !== slides[slideIndex]) {
                activeSlide.classList.add('variant-image-fade-out');
                slides[slideIndex].classList.add('variant-image-fade-in');
                
                // After transition, remove transition classes
                setTimeout(() => {
                  if (sliderTrack) sliderTrack.classList.remove('variant-image-transition');
                  activeSlide.classList.remove('variant-image-fade-out');
                  slides[slideIndex].classList.remove('variant-image-fade-in');
                }, 500);
              }
              
              // Try different methods to set the slide position
              if (typeof slider.setActiveSlide === 'function') {
                slider.setActiveSlide(slideIndex);
              } else if (typeof slider.setSlidePosition === 'function') {
                slider.setSlidePosition(slideIndex);
              } else {
                // Direct DOM manipulation as fallback
                // Try to find the slider track
                if (sliderTrack) {
                  const slideWidth = slides[0].offsetWidth;
                  sliderTrack.style.transform = `translate3d(${-slideWidth * slideIndex}px, 0, 0)`;
                  
                  // Also update active classes
                  slides.forEach((slide, i) => {
                    if (i === slideIndex) {
                      slide.classList.add('is-active', 'active', 'slideshow__slide--active');
                    } else {
                      slide.classList.remove('is-active', 'active', 'slideshow__slide--active');
                    }
                  });
                  
                  // If there's a counter, update it too
                  const counter = slider.querySelector('.slider-counter--current, .slideshow__text-wrapper');
                  if (counter) {
                    counter.textContent = slideIndex + 1;
                  }
                }
              }
            }
          }
        } else {
          // No slider, try direct media selection          
          // Try to find all media items
          const mediaItems = mediaGallery.querySelectorAll('.product__media');
          
          // Find the matching media item
          const matchingMedia = Array.from(mediaItems).find(
            item => item.dataset.mediaId === mediaId
          );
          
          // Find currently visible media
          const currentMedia = Array.from(mediaItems).find(
            item => !item.classList.contains('hidden') && item.classList.contains('active')
          );
          
          if (matchingMedia) {
            console.log(`Found matching direct media item with ID ${mediaId}`);
            
            // Only animate if not already showing this media
            if (currentMedia && currentMedia !== matchingMedia) {
              // Start transition
              currentMedia.classList.add('variant-image-fade-out');
              matchingMedia.classList.remove('hidden');
              matchingMedia.classList.add('variant-image-fade-in');
              
              // After transition completes, update final state
              setTimeout(() => {
                // Hide all media items except the matching one
                mediaItems.forEach(item => {
                  if (item !== matchingMedia) {
                    item.classList.add('hidden');
                    item.classList.remove('active', 'variant-image-fade-out');
                  }
                });
                
                // Remove transition class and ensure matching media is fully visible
                matchingMedia.classList.remove('variant-image-fade-in');
                matchingMedia.classList.add('active');
              }, 500);
            } else {
              // No transition needed, just show the matching media
              mediaItems.forEach(item => {
                item.classList.add('hidden');
                item.classList.remove('active');
              });
              
              matchingMedia.classList.remove('hidden');
              matchingMedia.classList.add('active');
            }
          }
        }
        
        // Also update thumbnails if they exist
        this.updateThumbnails(mediaId);
      }
      
      ensureTransitionCSS() {
        // Check if we already added the transition styles
        if (document.getElementById('variant-image-transitions')) {
          return;
        }
        
        // Create stylesheet for transitions
        const style = document.createElement('style');
        style.id = 'variant-image-transitions';
        style.textContent = `
          .variant-image-transition {
            transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
          }
          .variant-image-fade-out {
            opacity: 0 !important;
            transition: opacity 0.5s ease-out !important;
            z-index: 1;
          }
          .variant-image-fade-in {
            opacity: 1 !important;
            transition: opacity 0.5s ease-in !important;
            z-index: 2;
            position: relative;
          }
          .product__media {
            transition: opacity 0.5s ease-in-out !important;
          }
          .thumbnail, .product-thumbnail {
            transition: border-color 0.5s ease, opacity 0.5s ease, transform 0.5s ease !important;
          }
          .thumbnail.active, .product-thumbnail.active, 
          .thumbnail.is-active, .product-thumbnail.is-active {
            transform: scale(1.05) !important;
            opacity: 1 !important;
          }
          .thumbnail:not(.active):not(.is-active), 
          .product-thumbnail:not(.active):not(.is-active) {
            opacity: 0.7 !important;
          }
          .variant-image-transition-container {
            position: relative;
            overflow: hidden;
          }
        `;
        
        document.head.appendChild(style);
      }
      
      updateThumbnails(mediaId) {
        // Find all thumbnails
        const thumbnails = document.querySelectorAll('.thumbnail, .product-thumbnail, .thumbnail-list__item');
        
        if (thumbnails.length === 0) {
          return;
        }
                
        // Extract section ID if we have a media gallery with an ID
        const mediaGallery = document.querySelector('media-gallery');
        let sectionId = '';
        if (mediaGallery && mediaGallery.id && mediaGallery.id.includes('MediaGallery-')) {
          sectionId = mediaGallery.id.replace('MediaGallery-', '');
        }
        
        // Also try to find LI elements with data-target
        const thumbnailListItems = document.querySelectorAll('li[data-target], li[data-media-position]');
        
        // Find the matching thumbnail
        const matchingThumb = Array.from(thumbnails).find(
          thumb => {
            // Try different ways to match the thumbnail to the media ID
            if (thumb.dataset.mediaId === mediaId) return true;
            if (thumb.dataset.target === mediaId) return true;
            
            // Check if contains media ID with section ID prefix
            if (thumb.dataset.mediaId && thumb.dataset.mediaId.includes(`-${mediaId}`)) return true;
            if (thumb.dataset.target && thumb.dataset.target.includes(`-${mediaId}`)) return true;
            
            // Check if data-target matches section ID + media ID format
            if (sectionId && thumb.dataset.target === `${sectionId}-${mediaId}`) return true;
            
            // Check for LI ancestor that might have the ID
            const listItem = thumb.closest('li[data-target]');
            if (listItem && (
                listItem.dataset.target === mediaId || 
                listItem.dataset.target.includes(`-${mediaId}`) ||
                (sectionId && listItem.dataset.target === `${sectionId}-${mediaId}`)
            )) return true;
            
            // Check child elements
            return thumb.querySelector(`[data-media-id="${mediaId}"]`) || 
                  thumb.querySelector(`[data-media-id*="-${mediaId}"]`) ||
                  // Check buttons with aria-describedby that might match thumbnail ID
                  thumb.querySelector(`button[aria-describedby*="${mediaId}"]`);
          }
        );
        
        // If we couldn't find a matching thumbnail, try using list items
        let matchingListItem = null;
        if (!matchingThumb && thumbnailListItems.length > 0) {
          matchingListItem = Array.from(thumbnailListItems).find(
            item => {
              return item.dataset.target && (
                item.dataset.target === mediaId ||
                item.dataset.target.includes(`-${mediaId}`) ||
                (sectionId && item.dataset.target === `${sectionId}-${mediaId}`)
              );
            }
          );
        }
        
        if (matchingThumb || matchingListItem) {
          const foundItem = matchingThumb || matchingListItem;
          
          // First try to click the button inside the thumbnail to trigger the gallery's built-in mechanism
          const button = foundItem.querySelector('button');
          if (button) {
            // Use native click() instead of adding classes manually
            button.click();
            return; // Let the gallery's own code handle everything after click
          }
          
          // If there's no button, fallback to manually setting classes
          
          // First fade all thumbnails slightly
          thumbnails.forEach(thumb => {
            // Add subtle transition
            thumb.style.transition = 'opacity 0.5s ease, border-color 0.5s ease, transform 0.5s ease';
            // Fade non-active thumbs
            if (thumb !== foundItem) {
              thumb.classList.remove('active', 'is-active', 'selected', 'current-slide');
              
              // Also update aria-current on any buttons
              const thumbButton = thumb.querySelector('button');
              if (thumbButton) {
                thumbButton.removeAttribute('aria-current');
              }
            }
          });
          
          // Then highlight the active thumbnail with a slight delay for visual effect
          setTimeout(() => {
            // Add active class to matching thumbnail
            foundItem.classList.add('active', 'is-active');
            
            // Update aria attributes
            const foundButton = foundItem.querySelector('button');
            if (foundButton) {
              foundButton.setAttribute('aria-current', 'true');
            }
            
            // If the thumbnail is not visible (scrolled out of view), scroll to it
            const thumbsContainer = foundItem.closest('.thumbnail-list, .product-thumbnails');
            if (thumbsContainer) {
              const thumbsRect = thumbsContainer.getBoundingClientRect();
              const thumbRect = foundItem.getBoundingClientRect();
              
              // Check if thumbnail is outside visible area
              if (thumbRect.left < thumbsRect.left || thumbRect.right > thumbsRect.right) {
                thumbsContainer.scrollTo({
                  left: foundItem.offsetLeft - thumbsContainer.offsetWidth / 2 + foundItem.offsetWidth / 2,
                  behavior: 'smooth'
                });
              }
            }
          }, 50);
        } else {
          console.log(`No matching thumbnail found for media ID ${mediaId}`);
        }
      }

      updateAddToCartButton(variant) {
        const addButton = this.productElement.querySelector('.product-form__submit');
        if (!addButton) return;

        const buttonText = addButton.querySelector('span');
        const originalText = buttonText.dataset.originalText || buttonText.textContent;
        buttonText.dataset.originalText = originalText;

        if (!variant.available) {
          addButton.setAttribute('disabled', 'disabled');
          buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else {
          addButton.removeAttribute('disabled');
          buttonText.textContent = originalText;
        }
      }

      handleUnavailableVariant() {
        const addButton = this.productElement.querySelector('.product-form__submit');
        if (addButton) {
          addButton.setAttribute('disabled', 'disabled');
          const buttonText = addButton.querySelector('span');
          buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
        }
      }

      updateURL(variant) {
        if (!variant) return;
        
        const url = new URL(window.location.href);
        const variantUrl = `${url.pathname}?variant=${variant.id}`;
        
        window.history.replaceState({ path: variantUrl }, '', variantUrl);
      }

      formatMoney(cents) {
        // Try to use Shopify's money formatting if available
        if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
          console.log('Using Shopify.formatMoney with format:', window.theme?.moneyFormat || '${{amount}}');
          return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        }
        
        return `$${(cents/100).toFixed(2)}`;
      }

      getCurrentVariantFromURL() {
        const url = new URL(window.location.href);
        const variantId = url.searchParams.get('variant');
                
        if (variantId) {
          const variant = this.variantData.find(variant => variant.id.toString() === variantId);
          return variant;
        }
        
        return null;
      }

      buildOptionValueMap() {
        // Create a mapping of option values to variants for faster lookup
        this.optionValueMap = {};
        
        // First, add all option value IDs to map
        this.variantData.forEach(variant => {
          if (variant.option_value_ids) {
            variant.option_value_ids.forEach(valueId => {
              if (!this.optionValueMap[valueId]) {
                this.optionValueMap[valueId] = [];
              }
              this.optionValueMap[valueId].push(variant);
            });
          }
        });
        
        // Then add actual option values as strings
        this.variantData.forEach(variant => {
          if (variant.options) {
            variant.options.forEach(optionValue => {
              if (!this.optionValueMap[optionValue]) {
                this.optionValueMap[optionValue] = [];
              }
              this.optionValueMap[optionValue].push(variant);
            });
          }
        });
        
        // If we have productOptions with values that have names, add those too
        if (this.productOptions && this.productOptions.length) {
          this.productOptions.forEach(option => {
            if (option.values) {
              option.values.forEach(value => {
                const valueKey = value.value || value;
                if (!this.optionValueMap[valueKey]) {
                  this.optionValueMap[valueKey] = [];
                }
                
                // Find variants that match this option value
                this.variantData.forEach(variant => {
                  if (variant.options && variant.options.includes(valueKey)) {
                    if (!this.optionValueMap[valueKey].includes(variant)) {
                      this.optionValueMap[valueKey].push(variant);
                    }
                  }
                });
              });
            }
          });
        }
      }
    }
  );
}